import { useEffect, useState } from 'react';
import { usePlan } from '../store/planStore.js';
import { listPlans, type StoredDinner } from '../store/db.js';
import { useMeals } from '../recipes/useMeals.js';
import type { Rating } from '../store/library.js';

const CHOICES: { rating: Rating; label: string }[] = [
  { rating: 'loved', label: 'Loved it' },
  { rating: 'fine', label: 'It was fine' },
  { rating: 'never', label: 'Never again' },
];

export function AfterTheShop({ today }: { today: string }): React.JSX.Element | null {
  const { ready, plan, library, rateMeal } = usePlan();
  const { byId } = useMeals();
  const [otherDinners, setOtherDinners] = useState<StoredDinner[]>([]);

  /**
   * `weekStart` advances to next Monday before Sunday's own dinner can ever
   * satisfy `date < today` against the currently loaded week alone — so last
   * week's final meal was never rated, permanently. Reading every OTHER
   * stored week too is what lets a Sunday still get its prompt after the week
   * has rolled over. The currently loaded week always comes from live `plan`
   * state, never this fetch, so a dinner just planned is never missed while
   * its own autosave is still in flight.
   */
  useEffect(() => {
    if (!ready) return;
    let live = true;
    void (async () => {
      const stored = await listPlans();
      if (!live) return;
      setOtherDinners(
        stored.filter((p) => p.weekStart !== plan.weekStart).flatMap((p) => p.dinners),
      );
    })();
    return () => {
      live = false;
    };
  }, [ready, plan]);

  if (!ready) return null;

  /**
   * Answered OR dismissed. Both live in the cook log, because a skip held in
   * component state is undone the moment someone taps another tab — this
   * screen only renders on the plan view, so it unmounts constantly, and the
   * prompt would come back for something already waved away. That is the
   * nagging this whole design exists to avoid.
   */
  const settled = new Set(library.cookLog.map((e) => `${e.mealId}:${e.cookedOn}`));
  const candidates = [...otherDinners, ...plan.dinners].sort((a, b) => a.date.localeCompare(b.date));
  const pending = candidates.find(
    (d) => d.date < today && !settled.has(`${d.mealId}:${d.date}`) && byId.has(d.mealId),
  );
  if (!pending) return null;

  const meal = byId.get(pending.mealId);

  return (
    <aside className="after">
      <p className="after-q">How was {meal?.name ?? pending.mealId}?</p>
      <div className="after-row">
        {CHOICES.map(({ rating, label }) => (
          <button
            key={rating}
            type="button"
            onClick={() => rateMeal(pending.mealId, pending.date, rating)}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          className="ghost"
          onClick={() => rateMeal(pending.mealId, pending.date, null)}
        >
          Skip
        </button>
      </div>
    </aside>
  );
}
